// Consent for external media (YouTube/Vimeo), see template-parts/consent.php.
//
// - Nothing external loads before consent; iframes wait in data-src.
// - The choice is stored with the banner version and a timestamp and asked
//   again after 12 months or when the version (options page) is raised.
// - "Cookie-Einstellungen" in the footer reopens the banner with the current
//   choice; withdrawing media consent reloads the page to unload the embeds.
// - Buttons with data-consent-allow="media" (on the video placeholder) grant
//   consent in place.
const STORAGE_KEY = 'dana-consent';
const MAX_AGE = 365 * 24 * 60 * 60 * 1000;

const store = {
    read() {
        try {
            return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
        } catch {
            return null;
        }
    },
    write(value) {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
            // The previous banner's key; its consent covered other purposes.
            localStorage.removeItem('cookieNoticeNg');
        } catch {
            // Private mode: the choice holds for this page view only.
        }
    },
};

function apply(media) {
    document.documentElement.classList.toggle('has-consent', media);
    document.querySelectorAll('[data-consent-placeholder="media"]').forEach((el) => {
        el.hidden = media;
    });
    if (media) {
        document.querySelectorAll('iframe[data-src]').forEach((iframe) => {
            iframe.src = iframe.dataset.src;
            iframe.removeAttribute('data-src');
        });
        document.querySelectorAll('script[type="text/plain"][data-consent="media"]').forEach((blocked) => {
            const script = document.createElement('script');
            script.textContent = blocked.textContent;
            blocked.replaceWith(script);
        });
    }
    document.dispatchEvent(new CustomEvent('dana:consent', { detail: { media } }));
}

export function initConsent() {
    const banner = document.getElementById('consent');
    const version = parseInt(banner?.dataset.version || '1', 10);
    const options = banner?.querySelector('#consent-options');
    const mediaBox = banner?.querySelector('[data-consent-category="media"]');
    const toggle = banner?.querySelector('[data-consent-action="settings"]');
    const save = banner?.querySelector('[data-consent-action="save"]');

    const stored = store.read();
    const valid = stored && stored.v === version && Date.now() - stored.ts < MAX_AGE;
    let media = valid ? stored.media === true : false;

    const show = (withSettings) => {
        if (!banner) return;
        if (mediaBox) mediaBox.checked = media;
        setSettings(withSettings);
        banner.hidden = false;
    };
    const hide = () => {
        if (banner) banner.hidden = true;
    };
    const setSettings = (open) => {
        if (!options) return;
        options.hidden = !open;
        toggle?.setAttribute('aria-expanded', String(open));
        if (save) save.hidden = !open;
    };

    const decide = (granted) => {
        const wasGranted = media;
        media = granted;
        store.write({ v: version, media, ts: Date.now() });
        hide();
        if (wasGranted && !granted) {
            // Embeds that already loaded can only be removed by reloading.
            window.location.reload();
            return;
        }
        apply(media);
    };

    banner?.addEventListener('click', (event) => {
        const action = event.target.closest('[data-consent-action]')?.dataset.consentAction;
        if (action === 'accept') decide(true);
        if (action === 'reject') decide(false);
        if (action === 'save') decide(Boolean(mediaBox?.checked));
        if (action === 'settings') setSettings(options?.hidden ?? false);
    });

    document.addEventListener('click', (event) => {
        if (event.target.closest('[data-consent-open]')) {
            show(true);
            banner?.querySelector('#consent-title')?.setAttribute('tabindex', '-1');
            banner?.querySelector('#consent-title')?.focus();
        }
        if (event.target.closest('[data-consent-allow="media"]')) {
            decide(true);
        }
    });

    apply(media);
    if (!valid) show(false);
}
